import { useState, useRef, useCallback, useEffect } from 'react';
import maplibregl from 'maplibre-gl';
import { authFetch, getAuthHeaders } from '../lib/keycloak-api';

/**
 * Custom hook for managing Road Quality View
 * Fetches road quality data and renders roads with colors from API
 * Roads persist across pans and are only cleared on explicit toggle off
 * Every button click forces viewport to fixed bbox
 */
export const useRoadQuality = (mapRef) => {
  const [isRoadQualityEnabled, setIsRoadQualityEnabled] = useState(false);
  const [forceRefresh, setForceRefresh] = useState(0); // Trigger for forcing viewport reset
  const fetchTimeoutRef = useRef(null);
  const lastBoundsRef = useRef(null);
  const allFeaturesRef = useRef([]); // Store all fetched features (persistent)
  const roadIdsSetRef = useRef(new Set()); // Track unique road IDs for deduplication

  // Fetch road quality data from API
  const fetchRoadQualityData = useCallback(async (bounds) => {
    if (!bounds) return [];

    try {
      const north = bounds.getNorth();
      const south = bounds.getSouth();
      const east = bounds.getEast();
      const west = bounds.getWest();

      // API expects: top_lat (north), top_lon (west), bottom_lat (south), bottom_lon (east)
      // This is unusual naming but matches the API specification
      const url = `https://search-maps-pub-int.delhivery.com/rqi?top_lat=${north}&top_lon=${west}&bottom_lat=${south}&bottom_lon=${east}`;
      
      const headers = await getAuthHeaders();
      const response = await authFetch(url, {
        headers: {
          ...headers,
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.results || [];
    } catch (error) {
      return [];
    }
  }, []);

  // Parse WKT LINESTRING to GeoJSON
  const parseWKT = useCallback((wktString) => {
    try {
      // Parse LINESTRING format: "LINESTRING (lon1 lat1, lon2 lat2, ...)"
      const match = wktString.match(/LINESTRING\s*\((.*?)\)/i);
      if (!match) return null;

      const coordsString = match[1];
      const coordinates = coordsString
        .split(',')
        .map(pair => {
          const [lon, lat] = pair.trim().split(/\s+/).map(parseFloat);
          return [lon, lat];
        })
        .filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));

      if (coordinates.length < 2) return null;

      return {
        type: 'LineString',
        coordinates: coordinates
      };
    } catch (error) {
      return null;
    }
  }, []);

  // Update road quality layer (accumulative - roads persist)
  const updateRoadQualityLayer = useCallback(async () => {
    if (!mapRef.current || !isRoadQualityEnabled) {
      return;
    }

    const map = mapRef.current;
    const bounds = map.getBounds();

    // Check if bounds have changed significantly
    if (lastBoundsRef.current) {
      const lastBounds = lastBoundsRef.current;
      const boundsChanged = 
        Math.abs(bounds.getNorth() - lastBounds.getNorth()) > 0.001 ||
        Math.abs(bounds.getSouth() - lastBounds.getSouth()) > 0.001 ||
        Math.abs(bounds.getEast() - lastBounds.getEast()) > 0.001 ||
        Math.abs(bounds.getWest() - lastBounds.getWest()) > 0.001;

      if (!boundsChanged) {
        return;
      }
    }

    lastBoundsRef.current = bounds;

    // Fetch new data
    const results = await fetchRoadQualityData(bounds);

    // Convert results to GeoJSON features with deduplication
    results.forEach(result => {
      const roadId = result.road_id || result.id;
      
      // Skip if we've already added this road
      if (roadId && roadIdsSetRef.current.has(roadId)) {
        return;
      }

      const geometry = parseWKT(result.wkt || result.geometry);
      if (!geometry) return;

      const feature = {
        type: 'Feature',
        geometry: geometry,
        properties: {
          road_id: roadId,
          road_quality_score: result.road_quality_score,
          color: result.color,
          color_tag: result.color_tag
        }
      };

      // Add to our persistent collection
      allFeaturesRef.current.push(feature);
      if (roadId) {
        roadIdsSetRef.current.add(roadId);
      }
    });

    // Update the map with ALL accumulated features
    const geojson = {
      type: 'FeatureCollection',
      features: allFeaturesRef.current
    };

    // Add or update source
    if (map.getSource('road-quality')) {
      map.getSource('road-quality').setData(geojson);
    } else {
      map.addSource('road-quality', {
        type: 'geojson',
        data: geojson
      });

      // Add line layer for roads
      map.addLayer({
        id: 'road-quality-lines',
        type: 'line',
        source: 'road-quality',
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 4,
          'line-opacity': 0.8
        }
      });
    }
  }, [mapRef, isRoadQualityEnabled, fetchRoadQualityData, parseWKT]);

  // Clear road quality layer and reset all state
  const clearRoadQualityLayer = useCallback(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    // Remove layer and source
    if (map.getLayer('road-quality-lines')) {
      map.removeLayer('road-quality-lines');
    }

    if (map.getSource('road-quality')) {
      map.removeSource('road-quality');
    }

    // Clear all persistent data
    allFeaturesRef.current = [];
    roadIdsSetRef.current.clear();
    lastBoundsRef.current = null;

  }, [mapRef]);

  // Handle map movement
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const handleMoveEnd = () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      fetchTimeoutRef.current = setTimeout(() => {
        updateRoadQualityLayer();
      }, 300);
    };

    map.on('moveend', handleMoveEnd);

    return () => {
      if (map) {
        map.off('moveend', handleMoveEnd);
      }
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [mapRef, updateRoadQualityLayer]);

  // Handle enable/disable and force viewport on every button click
  useEffect(() => {
    if (!mapRef.current) return;

    if (isRoadQualityEnabled) {
      // Force viewport to fixed bbox EVERY TIME (even if already enabled)
      // API coordinates: top_lat=28.5, top_lon=77.0, bottom_lat=28.4, bottom_lon=77.1
      const targetBounds = new maplibregl.LngLatBounds(
        [77.0, 28.4], // Southwest [lon, lat] - bottom_lon, bottom_lat
        [77.1, 28.5]  // Northeast [lon, lat] - top_lon, top_lat
      );

      mapRef.current.fitBounds(targetBounds, {
        padding: 50,
        duration: 1500,
        essential: true
      });

      // Initial load after viewport adjustment
      setTimeout(() => {
        updateRoadQualityLayer();
      }, 1600); // Wait for pan to complete
    } else {
      // Clear when disabled
      clearRoadQualityLayer();
    }
  }, [isRoadQualityEnabled, forceRefresh, mapRef, updateRoadQualityLayer, clearRoadQualityLayer]);

  // Custom toggle function that forces viewport reset
  const toggleRoadQuality = useCallback(() => {
    if (isRoadQualityEnabled) {
      // Disable - clear everything
      setIsRoadQualityEnabled(false);
    } else {
      // Enable - force viewport reset
      setIsRoadQualityEnabled(true);
      setForceRefresh(prev => prev + 1); // Trigger viewport reset
    }
  }, [isRoadQualityEnabled]);

  return {
    isRoadQualityEnabled,
    setIsRoadQualityEnabled,
    toggleRoadQuality, // Use this instead of setIsRoadQualityEnabled for button clicks
    clearRoadQualityLayer,
    updateRoadQualityLayer
  };
};
